import { AfterViewInit, Component, Input } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FiltrosEstacaoMonitordada } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/interfaces/estacao-monitorada';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TipoEstacao } from '@modulos/meteorologia/submodulos/estacao/enums/tipo-estacao';
import { StatusEstacao } from '@modulos/meteorologia/submodulos/estacao/enums/status-estacao';
import { OpcaoFiltroEstacao } from '../../../../interfaces/monitoramento-estacoes';
import { map, merge } from 'rxjs';
import { OpcoesAgrupamentosFiltradosService } from '../../services/opcoes-agrupamentos-filtrados.service';
import {
	OpcoesAgrupamentosFiltradas,
	FiltroAgrupamentoDTO,
} from '@modulos/home/submodulos/dados-meteorologicos/interfaces/filtro-agrupamento';
import { ToastrService } from 'ngx-toastr';
import { isNotNuloOuUndefined, isNuloOuUndefined } from '@utils';

type CampoComFiltro =
	| 'bacia'
	| 'regiao'
	| 'municipio'
	| 'microrregiao'
	| 'mesorregiao';

@Component({
	selector: 'seira-modal-fitro-mapa-monitoramento',
	templateUrl: './modal-fitro-mapa-monitoramento.component.html',
	styleUrls: ['./modal-fitro-mapa-monitoramento.component.scss'],
})
export class ModalFitroMapaMonitoramentoComponent implements AfterViewInit {
	@Input() filtro!: any;
	@Input() tiposEstacao!: OpcaoFiltroEstacao[];
	@Input() baciasIniciais!: OpcaoFiltroEstacao[];
	@Input() municipiosIniciais!: OpcaoFiltroEstacao[];
	@Input() microrregioesIniciais!: OpcaoFiltroEstacao[];
	@Input() mesorregioesIniciais: OpcaoFiltroEstacao[] = [];
	@Input() regioesIniciais!: OpcaoFiltroEstacao[];
	@Input() enviarFiltro!: (filtro: any) => void;
	@Input() status: {
		label: StatusEstacao;
		value: keyof typeof StatusEstacao;
	}[] = [];
	formEstacao: FormGroup;
	opcoesAgrupamentos: OpcoesAgrupamentosFiltradas;
	isCarregandoOpcoesSelect = false;
	private ultimoCampoEsvaziado: CampoComFiltro | null;
	private _bacias!: OpcaoFiltroEstacao[];
	private _municipios!: OpcaoFiltroEstacao[];
	private _microrregioes!: OpcaoFiltroEstacao[];
	private _mesorregioes: OpcaoFiltroEstacao[] = [];
	private _regioes!: OpcaoFiltroEstacao[];
	private jaBuscouOpcoesDaAPI = false;

	constructor(
		public bsModalRef: BsModalRef,
		private formBuilder: FormBuilder,
		private filtrosAgrupamentoService: OpcoesAgrupamentosFiltradosService,
		private toastService: ToastrService
	) {
		this.formEstacao = this.formBuilder.group({
			tipoEstacao: new FormControl<TipoEstacao | null>(null),
			municipio: new FormControl<number | null>(null),
			bacia: new FormControl<number | null>(null),
			regiao: new FormControl<number | null>(null),
			microrregiao: new FormControl<number | null>(null),
			statusEstacao: new FormControl<StatusEstacao | null>(null),
			mesorregiao: new FormControl<number | null>(null),
		});

		for (const statusEstacaoKey in StatusEstacao) {
			const key = statusEstacaoKey as keyof typeof StatusEstacao;
			this.status.push({ value: key, label: StatusEstacao[key] });
		}
	}

	private get tipoEstacao() {
		return this.formEstacao.get('tipoEstacao')?.value;
	}

	private get bacia() {
		return this.formEstacao.get('bacia')?.value;
	}

	private get municipio() {
		return this.formEstacao.get('municipio')?.value;
	}

	private get regiao() {
		return this.formEstacao.get('regiao')?.value;
	}

	private get microrregiao() {
		return this.formEstacao.get('microrregiao')?.value;
	}

	private get mesorregiao() {
		return this.formEstacao.get('mesorregiao')?.value;
	}

	get bacias(): OpcaoFiltroEstacao[] {
		return this._bacias;
	}

	get municipios(): OpcaoFiltroEstacao[] {
		return this._municipios;
	}

	get microrregioes(): OpcaoFiltroEstacao[] {
		return this._microrregioes;
	}

	get mesorregioes(): OpcaoFiltroEstacao[] {
		return this._mesorregioes;
	}

	get regioes(): OpcaoFiltroEstacao[] {
		return this._regioes;
	}

	private set tipoEstacao(value: any) {
		this.setarValor('tipoEstacao', value);
	}

	private set bacia(value: any) {
		this.setarValor('bacia', value);
	}

	private set municipio(value: any) {
		this.setarValor('municipio', value);
	}

	private set regiao(value: any) {
		this.setarValor('regiao', value);
	}

	private set microrregiao(value: any) {
		this.setarValor('microrregiao', value);
	}

	private set mesorregiao(value: any) {
		this.setarValor('mesorregiao', value);
	}

	private set municipios(municipios: OpcaoFiltroEstacao[]) {
		this._municipios = municipios;
		if (
			this._municipios.length === 1 &&
			this.ultimoCampoEsvaziado !== 'municipio'
		) {
			this.municipio = this._municipios[0].id;
		}
	}

	private set microrregioes(microrregioes: OpcaoFiltroEstacao[]) {
		this._microrregioes = microrregioes;
		if (
			this._microrregioes.length === 1 &&
			this.ultimoCampoEsvaziado !== 'microrregiao'
		) {
			this.microrregiao = this._microrregioes[0].id;
		}
	}

	private set mesorregioes(mesorregioes: OpcaoFiltroEstacao[]) {
		this._mesorregioes = mesorregioes;
		if (
			this._mesorregioes.length === 1 &&
			this.ultimoCampoEsvaziado !== 'mesorregiao'
		) {
			this.mesorregiao = this.mesorregioes[0].id;
		}
	}

	private set bacias(bacias: OpcaoFiltroEstacao[]) {
		this._bacias = bacias;
	}

	private set regioes(regioes: OpcaoFiltroEstacao[]) {
		this._regioes = regioes;
		if (this._regioes.length === 1 && this.ultimoCampoEsvaziado !== 'regiao') {
			this.regiao = this.regioes[0].id;
		}
	}

	ngAfterViewInit(): void {
		if (
			this.filtro.estacaoId ||
			this.filtro.tipoEstacao ||
			this.filtro.mesorregiaoId ||
			this.filtro.microrregiaoId ||
			this.filtro.municipioId ||
			this.filtro.regiaoId ||
			this.filtro.baciaId ||
			this.filtro.statusEstacao
		) {
			this.popularCampos();
			this.fetchOpcoesAgrupamentosFiltradas();
		} else {
			this.atribuirOpcoesIniciaisSelects();
		}
		this.ouvirMudancasSelects();
	}

	private ouvirMudancasSelects(): void {
		const opcoes: CampoComFiltro[] = [
			'bacia',
			'regiao',
			'municipio',
			'microrregiao',
			'mesorregiao',
		];
		merge(
			...opcoes.map(
				nomeCampo =>
					this.formEstacao
						.get(nomeCampo)
						?.valueChanges.pipe(map(valor => ({ nomeCampo, valor })))
			)
		).subscribe({
			next: ({ nomeCampo, valor }) => {
				if (isNuloOuUndefined(valor)) {
					this.ultimoCampoEsvaziado = nomeCampo;
				}
				this.fetchOpcoesAgrupamentosFiltradas();
				this.ultimoCampoEsvaziado = isNuloOuUndefined(valor) ? nomeCampo : null;
			},
		});
	}

	private getIdsEntidades(): FiltroAgrupamentoDTO[] {
		return [
			{ agrupamento: 'BACIA', idEntidade: this.bacia },
			{ agrupamento: 'MUNICIPIO', idEntidade: this.municipio },
			{ agrupamento: 'MESORREGIAO', idEntidade: this.mesorregiao },
			{ agrupamento: 'MICRORREGIAO', idEntidade: this.microrregiao },
			{ agrupamento: 'REGIAO_PLUVIOMETRICA', idEntidade: this.regiao },
		].filter(e => isNotNuloOuUndefined(e.idEntidade));
	}

	private fetchOpcoesAgrupamentosFiltradas(): void {
		this.isCarregandoOpcoesSelect = true;
		this.filtrosAgrupamentoService.getOpcoes(this.getIdsEntidades()).subscribe({
			next: res => {
				this.opcoesAgrupamentos = res;
				this.bacias = this.baciasIniciais.filter(
					e => isNuloOuUndefined(res.bacias) || res.bacias.includes(e.id)
				);
				this.municipios = this.municipiosIniciais.filter(
					e =>
						isNuloOuUndefined(res.municipios) || res.municipios.includes(e.id)
				);
				this.microrregioes = this.microrregioesIniciais.filter(
					e =>
						isNuloOuUndefined(res.microrregioes) ||
						res.microrregioes.includes(e.id)
				);
				this.mesorregioes = this.mesorregioesIniciais.filter(
					e =>
						isNuloOuUndefined(res.mesorregioes) ||
						res.mesorregioes.includes(e.id)
				);
				this.regioes = this.regioesIniciais.filter(
					e => isNuloOuUndefined(res.regioes) || res.regioes.includes(e.id)
				);
				if (this.jaBuscouOpcoesDaAPI) {
					this.isCarregandoOpcoesSelect = false;
				} else {
					this.jaBuscouOpcoesDaAPI = true;
					this.fetchOpcoesAgrupamentosFiltradas();
				}
			},
			error: () => {
				this.toastService.error(
					'Não foi possível trazer opções filtradas para a entidade selecionada',
					'Erro ao trazer opções'
				);
				this.isCarregandoOpcoesSelect = false;
			},
		});
	}

	salvarFiltros(): void {
		const { tipoEstacao, mesorregiao, municipio, bacia, regiao, microrregiao } =
			this.formEstacao.getRawValue();

		const novoFiltro = new FiltrosEstacaoMonitordada(
			null,
			tipoEstacao,
			mesorregiao,
			municipio,
			null,
			bacia,
			regiao,
			microrregiao
		);
		this.enviarFiltro(novoFiltro);
		this.bsModalRef.hide();
	}

	existeValoresCampos(): boolean {
		return (
			this.tipoEstacao ||
			this.bacia ||
			this.municipio ||
			this.microrregiao ||
			this.mesorregiao ||
			this.regiao
		);
	}

	limparFiltros(): void {
		this.bacia =
			this.municipio =
			this.microrregiao =
			this.mesorregiao =
			this.regiao =
			this.tipoEstacao =
				null;
		this.atribuirOpcoesIniciaisSelects();
	}

	private atribuirOpcoesIniciaisSelects(): void {
		this.bacias = [...this.baciasIniciais];
		this.mesorregioes = [...this.mesorregioesIniciais];
		this.microrregioes = [...this.microrregioesIniciais];
		this.regioes = [...this.regioesIniciais];
		this.municipios = [...this.municipiosIniciais];
	}

	private popularCampos(): void {
		this.formEstacao.setValue(
			{
				tipoEstacao: this.filtro.tipoEstacao,
				municipio: this.filtro.municipioId,
				bacia: this.filtro.baciaId,
				regiao: this.filtro.regiaoId,
				microrregiao: this.filtro.microrregiaoId,
				statusEstacao: this.filtro.statusEstacao,
				mesorregiao: this.filtro.mesorregiaoId,
			},
			{ emitEvent: false }
		);
	}

	private setarValor(nomeCampo: string, valor: any): void {
		const controle = this.formEstacao.get(nomeCampo)!;
		controle.setValue(valor, { emitEvent: false });
	}
}
